import { useTranslation } from 'react-i18next'

const EMPTY_ADDRESS = {
  type: 'home',
  street: '',
  city: '',
  postalCode: '',
  country: 'Polska',
  paczkomatId: '',
}

export function normalizeAddress(value) {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_ADDRESS }
  }
  return {
    type: value.type || EMPTY_ADDRESS.type,
    street: value.street || '',
    city: value.city || '',
    postalCode: value.postalCode || value.postal_code || '',
    country: value.country || EMPTY_ADDRESS.country,
    paczkomatId: value.paczkomatId || value.paczkomat_id || '',
  }
}

export function isAddressComplete(address) {
  const a = normalizeAddress(address)
  if (a.type === 'paczkomat') {
    return a.paczkomatId.trim().length > 0
  }
  return (
    a.street.trim().length > 0 &&
    a.city.trim().length > 0 &&
    a.postalCode.trim().length > 0 &&
    a.country.trim().length > 0
  )
}

export function formatAddress(address) {
  const a = normalizeAddress(address)
  if (a.type === 'paczkomat') {
    return a.paczkomatId
  }
  const parts = [a.street, `${a.postalCode} ${a.city}`, a.country].filter(Boolean)
  return parts.join(', ')
}

function Label({ children }) {
  return (
    <label className="block text-sm font-semibold text-brand-blue mb-1 font-body">
      {children}
    </label>
  )
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-white rounded-xl px-4 py-2.5 text-base text-brand-blue placeholder-brand-blue/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold border border-brand-blue/10 font-body"
    />
  )
}

function Radio({ name, value, checked, onChange, label }) {
  return (
    <label
      className={`flex-1 cursor-pointer rounded-xl border px-4 py-3 text-center text-sm font-semibold transition font-body ${
        checked
          ? 'border-brand-gold bg-brand-gold/10 text-brand-blue'
          : 'border-brand-blue/10 bg-white/60 text-brand-blue/70 hover:bg-white'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      {label}
    </label>
  )
}

/**
 * Reusable shipping-address / Paczkomat form.
 *
 * Props:
 * - value: current address object
 * - onChange: (address) => void
 * - disabled: boolean
 */
export default function ShippingAddressForm({ value, onChange, disabled = false }) {
  const { t } = useTranslation()
  const address = normalizeAddress(value)

  const handleChange = (field, newValue) => {
    onChange(normalizeAddress({ ...address, [field]: newValue }))
  }

  const handleTypeChange = (type) => {
    onChange(normalizeAddress({ ...EMPTY_ADDRESS, type }))
  }

  return (
    <div className="space-y-4 font-body">
      <div className="flex gap-3">
        <Radio
          name="addressType"
          value="home"
          checked={address.type === 'home'}
          onChange={handleTypeChange}
          label={t('checkout.address.home', 'Adres domowy')}
        />
        <Radio
          name="addressType"
          value="paczkomat"
          checked={address.type === 'paczkomat'}
          onChange={handleTypeChange}
          label={t('checkout.address.paczkomat', 'Paczkomat')}
        />
      </div>

      {address.type === 'home' ? (
        <div className="space-y-4">
          <div>
            <Label>{t('library.profile.address.street', 'Ulica i numer')}</Label>
            <Input
              type="text"
              value={address.street}
              onChange={(e) => handleChange('street', e.target.value)}
              placeholder={t('checkout.address.streetPlaceholder', 'np. ul. Marszałkowska 1/2')}
              disabled={disabled}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('library.profile.address.postalCode', 'Kod pocztowy')}</Label>
              <Input
                type="text"
                value={address.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
                placeholder={t('checkout.address.postalCodePlaceholder', 'np. 00-001')}
                disabled={disabled}
              />
            </div>
            <div>
              <Label>{t('library.profile.address.city', 'Miasto')}</Label>
              <Input
                type="text"
                value={address.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder={t('checkout.address.cityPlaceholder', 'np. Warszawa')}
                disabled={disabled}
              />
            </div>
          </div>
          <div>
            <Label>{t('library.profile.address.country', 'Kraj')}</Label>
            <Input
              type="text"
              value={address.country}
              onChange={(e) => handleChange('country', e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      ) : (
        <div>
          <Label>{t('checkout.address.paczkomatId', 'Numer Paczkomatu')}</Label>
          <Input
            type="text"
            value={address.paczkomatId}
            onChange={(e) => handleChange('paczkomatId', e.target.value.toUpperCase())}
            placeholder={t('checkout.address.paczkomatIdPlaceholder', 'np. WAW01A')}
            disabled={disabled}
          />
          <p className="text-sm text-brand-blue/60 mt-2">
            {t('checkout.address.paczkomatHint', 'Podaj numer wybranego Paczkomatu. Adres dostawy zostanie ustalony automatycznie.')}
          </p>
        </div>
      )}
    </div>
  )
}
